import React, { useState } from "react";
import { FiZap, FiChevronDown, FiChevronRight } from "react-icons/fi";

/**
 * Permissions Selector Component
 *
 * Displays permissions grouped by category, fetched directly from auth service
 * Acts as the source of truth for all permission selections
 */
const PermissionsSelector = ({
  permissionsByCategory = {},
  selectedPermissions = [],
  onChange,
  loading = false,
  aiPermissions = [],
  showAIIndicator = true,
  maxHeight = "300px",
}) => {
  const [expandedCategories, setExpandedCategories] = useState({});
  const [searchTerm, setSearchTerm] = useState("");

  // Initialize first category as expanded
  React.useEffect(() => {
    if (Object.keys(permissionsByCategory).length > 0) {
      const firstCategory = Object.keys(permissionsByCategory)[0];
      setExpandedCategories((prev) => ({
        ...prev,
        [firstCategory]: true,
      }));
    }
  }, [permissionsByCategory]);

  const toggleCategory = (category) => {
    setExpandedCategories((prev) => ({
      ...prev,
      [category]: !prev[category],
    }));
  };

  const togglePermission = (permissionName) => {
    const newSelection = selectedPermissions.includes(permissionName)
      ? selectedPermissions.filter((p) => p !== permissionName)
      : [...selectedPermissions, permissionName];
    onChange(newSelection);
  };

  const selectAllInCategory = (category, perms) => {
    const categoryPermNames = perms.map((p) => p.name);
    const allSelected = categoryPermNames.every((p) =>
      selectedPermissions.includes(p),
    );

    let newSelection;
    if (allSelected) {
      // Remove all from this category
      newSelection = selectedPermissions.filter(
        (p) => !categoryPermNames.includes(p),
      );
    } else {
      // Add all from this category
      newSelection = [...selectedPermissions];
      categoryPermNames.forEach((p) => {
        if (!newSelection.includes(p)) {
          newSelection.push(p);
        }
      });
    }
    onChange(newSelection);
  };

  const filterPermissions = (perms) => {
    if (!searchTerm) return perms;
    return perms.filter((perm) =>
      perm.name.toLowerCase().includes(searchTerm.toLowerCase()),
    );
  };

  if (loading) {
    return (
      <div
        style={{
          padding: "20px",
          textAlign: "center",
          color: "var(--text-secondary)",
          border: "1px solid var(--border-color)",
          borderRadius: "8px",
          background: "var(--bg-tertiary)",
        }}
      >
        <div
          className="spinner"
          style={{
            width: "20px",
            height: "20px",
            borderWidth: "2px",
            margin: "0 auto 8px",
          }}
        ></div>
        <span>Loading permissions from auth service...</span>
      </div>
    );
  }

  if (Object.keys(permissionsByCategory).length === 0) {
    return (
      <div
        style={{
          padding: "20px",
          textAlign: "center",
          color: "var(--text-secondary)",
          border: "1px solid var(--border-color)",
          borderRadius: "8px",
          background: "var(--bg-tertiary)",
        }}
      >
        <p>No permissions found for this application</p>
        <p style={{ fontSize: "12px", marginTop: "4px" }}>
          Permissions are fetched directly from auth service
        </p>
      </div>
    );
  }

  return (
    <div className="permissions-selector">
      {/* Search Bar */}
      <div style={{ marginBottom: "12px" }}>
        <input
          type="text"
          placeholder="Search permissions..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          style={{
            width: "100%",
            padding: "8px 12px",
            border: "1px solid var(--border-color)",
            borderRadius: "6px",
            fontSize: "14px",
          }}
        />
      </div>

      {/* Permissions List */}
      <div
        style={{
          maxHeight,
          overflowY: "auto",
          border: "1px solid var(--border-color)",
          borderRadius: "8px",
          background: "var(--bg-tertiary)",
          padding: "8px",
        }}
      >
        {Object.entries(permissionsByCategory)
          .sort(([a], [b]) => a.localeCompare(b))
          .map(([category, perms]) => {
            const filteredPerms = filterPermissions(perms);
            if (filteredPerms.length === 0) return null;

            const allCategorySelected = filteredPerms.every((p) =>
              selectedPermissions.includes(p.name),
            );
            const someCategorySelected = filteredPerms.some((p) =>
              selectedPermissions.includes(p.name),
            );

            return (
              <div key={category} style={{ marginBottom: "8px" }}>
                {/* Category Header */}
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    padding: "8px 12px",
                    background: "var(--bg-secondary)",
                    borderRadius: "6px",
                    cursor: "pointer",
                    fontWeight: 500,
                    fontSize: "14px",
                    border: "1px solid var(--border-color)",
                    marginBottom: expandedCategories[category] ? "8px" : 0,
                  }}
                  onClick={() => toggleCategory(category)}
                >
                  <span style={{ marginRight: "8px" }}>
                    {expandedCategories[category] ? (
                      <FiChevronDown />
                    ) : (
                      <FiChevronRight />
                    )}
                  </span>
                  <span style={{ textTransform: "capitalize" }}>
                    {category} ({filteredPerms.length})
                  </span>
                  <span style={{ flex: 1 }} />
                  <div
                    onClick={(e) => {
                      e.stopPropagation();
                      selectAllInCategory(category, filteredPerms);
                    }}
                    style={{
                      fontSize: "12px",
                      color: "var(--primary)",
                      cursor: "pointer",
                      marginLeft: "8px",
                      padding: "2px 8px",
                      borderRadius: "4px",
                      background: "rgba(99, 102, 241, 0.1)",
                    }}
                  >
                    {allCategorySelected ? "Deselect All" : "Select All"}
                  </div>
                </div>

                {/* Category Permissions */}
                {expandedCategories[category] && (
                  <div
                    style={{
                      display: "grid",
                      gridTemplateColumns:
                        "repeat(auto-fill, minmax(250px, 1fr))",
                      gap: "4px",
                      padding: "8px 8px 8px 28px",
                    }}
                  >
                    {filteredPerms.map((perm) => (
                      <label
                        key={perm.name}
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "8px",
                          padding: "6px 10px",
                          borderRadius: "6px",
                          cursor: "pointer",
                          fontSize: "13px",
                          transition: "background 0.15s",
                          background: selectedPermissions.includes(perm.name)
                            ? "rgba(99, 102, 241, 0.08)"
                            : "transparent",
                        }}
                        onMouseEnter={(e) => {
                          if (!selectedPermissions.includes(perm.name)) {
                            e.currentTarget.style.background =
                              "var(--bg-secondary)";
                          }
                        }}
                        onMouseLeave={(e) => {
                          if (!selectedPermissions.includes(perm.name)) {
                            e.currentTarget.style.background = "transparent";
                          }
                        }}
                      >
                        <input
                          type="checkbox"
                          checked={selectedPermissions.includes(perm.name)}
                          onChange={() => togglePermission(perm.name)}
                          style={{ margin: 0 }}
                        />
                        <span style={{ flex: 1 }}>
                          {perm.name}
                          {showAIIndicator &&
                            aiPermissions.includes(perm.name) && (
                              <FiZap
                                style={{
                                  color: "var(--warning)",
                                  marginLeft: "4px",
                                  fontSize: "11px",
                                  verticalAlign: "middle",
                                }}
                                title="AI Feature - consumes tokens"
                              />
                            )}
                        </span>
                      </label>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
      </div>

      {/* Selection Summary */}
      <div
        style={{
          marginTop: "12px",
          padding: "8px 12px",
          background: "rgba(99, 102, 241, 0.08)",
          borderRadius: "6px",
          fontSize: "13px",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <span>
          <strong>{selectedPermissions.length}</strong> permissions selected
        </span>
        {selectedPermissions.length > 0 && (
          <button
            onClick={() => onChange([])}
            style={{
              background: "none",
              border: "none",
              color: "var(--primary)",
              cursor: "pointer",
              fontSize: "12px",
              textDecoration: "underline",
            }}
          >
            Clear all
          </button>
        )}
      </div>

      {/* Auth Service Source Note */}
      <div
        style={{
          marginTop: "8px",
          fontSize: "11px",
          color: "var(--text-secondary)",
          textAlign: "right",
        }}
      >
        <span>Permissions fetched from auth service (source of truth)</span>
      </div>
    </div>
  );
};

export default PermissionsSelector;
